import { ErrorMessage } from '@components/Form'
import { ToggleSwitch } from '@features/operations/components/ToggleSwitch'
import { updateLLMDataSharingStatusApi } from 'api-validations/user-api'
import type React from 'react'
import { useState } from 'react'
import {
  OrgLevelPermissions,
  useAccount,
  useIsAuroraEnabled,
  useOrgCodeOrUndefined,
  useOrgInfo
} from 'shared-ui/active-operation'
import { usePanoramaMutation } from 'shared-ui/api-provider'
import { ButtonKind, Icon, PanoramaButton, Popover } from 'shared-ui/components'
import { SpriteMap, SpriteMapPredefined } from 'shared-ui/global'
import { useLocalization } from 'shared-ui/localization'
import { isNonNil } from 'utils'

import { useAuroraChatStore } from '../auroraChatStore'

export const AuroraNavButton: React.FC = () => {
  const orgCode = useOrgCodeOrUndefined()
  const orgInfo = useOrgInfo()
  const isAuroraEnabled = useIsAuroraEnabled()
  const account = useAccount()
  const { t } = useLocalization()
  const toggleAuroraChat = useAuroraChatStore((s) => s.toggle)
  const [isDataSharingPopoverOpen, setIsDataSharingPopoverOpen] = useState(false)
  const llmSharingStatus = usePanoramaMutation(updateLLMDataSharingStatusApi, {
    onSuccess: () => setIsDataSharingPopoverOpen(false)
  })

  if (orgCode === undefined || account.isPending) return null

  const isSupporter = isNonNil(orgInfo) && OrgLevelPermissions.isSupporter(orgInfo)

  if (isAuroraEnabled) {
    return (
      <button
        onClick={toggleAuroraChat}
        aria-label={t(($) => $['aurora-chat-title'])}
        title={t(($) => $['aurora-chat-title'])}
        className="flex size-10 shrink-0 items-center justify-center"
      >
        <Icon icon={SpriteMapPredefined.AuroraPanoramaPredefined} className="size-6" />
      </button>
    )
  }

  return (
    <Popover
      open={isDataSharingPopoverOpen}
      onOpenChange={(open) => {
        if (open) llmSharingStatus.reset()
        setIsDataSharingPopoverOpen(open)
      }}
      align="end"
      showArrow={false}
      trigger={
        <button
          aria-label={t(($) => $['aurora-llm-data-sharing'])}
          title={t(($) => $['aurora-llm-data-sharing'])}
          className="flex size-10 shrink-0 items-center justify-center"
        >
          <Icon icon={SpriteMap.AuroraPanoramaIcon} className="size-6 text-white" />
        </button>
      }
    >
      <div className="flex w-[454px] flex-col gap-6 rounded-lg border border-black/15 bg-white p-6">
        <div className="flex w-full items-start justify-between">
          <div className="flex items-center gap-3">
            <Icon icon={SpriteMapPredefined.AuroraPanoramaPredefined} className="size-6" />
            <p className="font-body text-xl font-bold text-gray-900">
              {t(($) => $['aurora-llm-data-sharing'])}
            </p>
          </div>
          <ToggleSwitch
            checked={isAuroraEnabled}
            onChange={(enabled) => llmSharingStatus.mutate({ params: { orgCode, enabled } })}
            disabled={isSupporter || llmSharingStatus.isPending}
          />
        </div>
        <p className="pan-body w-full text-gray-900">{t(($) => $['aurora-opt-out-description'])}</p>
        {llmSharingStatus.isError && (
          <ErrorMessage>{t(($) => $['aurora-llm-data-sharing-update-error'])}</ErrorMessage>
        )}
        <PanoramaButton
          kind={ButtonKind.Secondary}
          className="w-full"
          onClick={() => setIsDataSharingPopoverOpen(false)}
        >
          {t(($) => $.labels.ok)}
        </PanoramaButton>
      </div>
    </Popover>
  )
}
